import { Device } from "@twilio/voice-sdk";
import axios from "axios";

// Creates and registers a persistent Voice Device for the given identity,
// so this browser session can RECEIVE in-app calls, not just place them.
// This is separate from the SMS-chat voice flow, which only ever creates
// a Device on-demand at the moment of an outgoing call.
export const initializeIdentityVoiceDevice = async ({
  userId,
  getAccessTokenSilently,
  onIncomingCall,
  onDeviceReady,
  onDeviceError,
}) => {
  try {
    const authToken = await getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0_AUDIENCE,
    });

    const response = await axios.get(
      process.env.REACT_APP_COMMUNICATION_NODE_API_URL +
        "voice/get-call-token/" +
        userId,
      {
        headers: { Authorization: `Bearer ${authToken}` },
      }
    );

    const twilioToken = response?.data?.data?.token;
    if (!twilioToken) {
      console.log("[IDENTITY VOICE] No token returned from get-call-token");
      onDeviceError && onDeviceError(new Error("No token returned"));
      return null;
    }

   // AFTER
    const device = new Device(twilioToken, {
      allowIncomingWhileBusy: false,
    });

    let errorGraceTimer = null;
    let recoveredAfterError = false;

    device.on("registered", () => {
      console.log("[IDENTITY VOICE] Device registered and ready to receive calls");
      recoveredAfterError = true;
      if (errorGraceTimer) {
        clearTimeout(errorGraceTimer);
        errorGraceTimer = null;
      }
      onDeviceReady && onDeviceReady(device);
    });

    device.on("error", (error) => {
      console.log("[IDENTITY VOICE] Device error", error);
      recoveredAfterError = false;
      if (errorGraceTimer) clearTimeout(errorGraceTimer);
      errorGraceTimer = setTimeout(() => {
        if (!recoveredAfterError) {
          onDeviceError && onDeviceError(error);
        }
      }, 5000);
    });

    device.on("incoming", (call) => {
      console.log("[IDENTITY VOICE] Incoming call from", call.parameters?.From);
      onIncomingCall && onIncomingCall(call);
    });

    await device.register();

    return device;
  } catch (error) {
    console.log("[IDENTITY VOICE] initializeIdentityVoiceDevice error", error);
    onDeviceError && onDeviceError(error);
    return null;
  }
};

// Places an outgoing in-app call to another employee's identity.
// No phone-number formatting — the target is a raw identity string.
export const placeIdentityVoiceCall = (device, targetIdentity) => {
  if (!device) {
    throw new Error("Voice device is not initialized yet");
  }
  return device.connect({
    params: {
      To: String(targetIdentity),
      isInAppVoiceCall: "true",
    },
  });
};